function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import produce from 'immer';
import { billingTrigger, isRecurring, periodRequiresBillingDayOfWeek, isMonthlyService, isOngoing, effectiveDate, isAmended, isAwaitingApproval, isCompleted, changes } from "./selectors";
import { makeAmendedStatus, makeApprovedStatus, makeAwaitingApprovalStatus, makeCanceledStatus, makeCompletedStatus, makeMaxCharges, makeTerminatedStatus, makeChanges, makeBillUpfrontState } from "./makers";
export function setBillingTrigger(service, billingTrigger) {
  return _objectSpread(_objectSpread({}, service), {}, {
    billing: billingTrigger
  });
}
export function setBillUpfront(service, billUpfront) {
  return produce(service, s => {
    const billingTrigger = s.billing;

    if (isOngoing(billingTrigger)) {
      billingTrigger.isBilledUpfront = billUpfront;
      billingTrigger.billUpfrontState = makeBillUpfrontState(true);
    }
  });
}
export function setSkipBillingOnAcceptance(service, skipBillingOnAcceptance) {
  return produce(service, s => {
    const billingTrigger = s.billing;

    if (isOngoing(billingTrigger)) {
      billingTrigger.billUpfrontState = makeBillUpfrontState(skipBillingOnAcceptance);
      billingTrigger.isProrated = false;
    }
  });
}
export function setBillingDayOfWeek(service, billingDayOfWeek) {
  const newBilling = setBillingDayOfWeekOnBilling(service.billing, billingDayOfWeek);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setBillingDayOfWeekOnBilling(billing, billingDayOfWeek) {
  return isRecurring(billing) && periodRequiresBillingDayOfWeek(billing.recurrencePeriod) ? _objectSpread(_objectSpread({}, billing), {}, {
    billingDayOfWeek
  }) : billing;
}
export function setBillingDayOfMonth(service, billingDayOfMonth) {
  const newBilling = setBillingDayOfMonthOnBilling(service.billing, billingDayOfMonth);
  return newBilling !== service.billing ? _objectSpread(_objectSpread({}, service), {}, {
    billing: newBilling
  }) : service;
}
export function setBillingDayOfMonthOnBilling(billing, billingDayOfMonth) {
  return isMonthlyService({
    billing
  }) && isRecurring(billing) ? _objectSpread(_objectSpread({}, billing), {}, {
    billingDayOfMonth
  }) : billing;
}
export function setMaxCharges(service, enabled, limit) {
  const trigger = billingTrigger(service);

  if (isRecurring(trigger)) {
    return setBillingTrigger(service, _objectSpread(_objectSpread({}, trigger), {}, {
      maxCharges: {
        enabled,
        limit
      }
    }));
  } else {
    return service;
  }
}
export function deleteMaxCharges(service) {
  return produce(service, s => {
    const trigger = billingTrigger(s);

    if (isRecurring(trigger)) {
      trigger.maxCharges = makeMaxCharges(false);
    }
  });
}
export function setPaused(service, isPaused) {
  const trigger = billingTrigger(service);

  if (isOngoing(trigger)) {
    return setBillingTrigger(service, _objectSpread(_objectSpread({}, trigger), {}, {
      isPaused: isPaused
    }));
  } else {
    return service;
  }
}
export function setProrated(service, isProrated) {
  const trigger = billingTrigger(service);

  if (isOngoing(trigger)) {
    return setBillingTrigger(service, _objectSpread(_objectSpread({}, trigger), {}, {
      isProrated: isProrated
    }));
  } else {
    return service;
  }
}
export function setTerminated(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeTerminatedStatus(effectiveDate(service) || new Date())
  });
}
export function setCanceled(service) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeCanceledStatus()
  });
}
export function setAwaitingApproval(service, options) {
  return _objectSpread(_objectSpread({}, service), {}, {
    status: makeAwaitingApprovalStatus(options)
  });
}
export function discardChanges(service) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeApprovedStatus(effectiveDate(service) || new Date())
    });
  } else if (isCompleted(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        completed: {}
      })
    });
  } else {
    return service;
  }
}
export function setChanges(service, changes) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus(_objectSpread(_objectSpread({
        date: service.status.date
      }, service.status.amended), {}, {
        changes
      }))
    });
  } else if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus({
        date: effectiveDate(service),
        approvalMethod: service.status.awaitingApproval.approvalMethod,
        amendedOn: service.status.awaitingApproval.addedOn,
        changes,
        note: service.status.awaitingApproval.note
      })
    });
  } else if (isCompleted(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeCompletedStatus(service.status.date, changes)
    });
  } else {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: makeAmendedStatus({
        changes,
        date: effectiveDate(service)
      })
    });
  }
}
export function setBillingChanges(service, billing) {
  const existingChanges = changes(service);
  const newChanges = existingChanges ? _objectSpread(_objectSpread({}, existingChanges), {}, {
    billing
  }) : makeChanges({
    billing
  });
  return setChanges(service, newChanges);
}
export function setBillingDayOfWeekChanges(service, dayOfWeek) {
  const newBilling = setBillingDayOfWeekOnBilling(applyChanges(service).billing, dayOfWeek);
  return setBillingChanges(service, newBilling);
}
export function setBillingDayOfMonthChanges(service, dayOfMonth) {
  const newBilling = setBillingDayOfMonthOnBilling(applyChanges(service).billing, dayOfMonth);
  return setBillingChanges(service, newBilling);
}
export function setNote(service, note) {
  if (isAmended(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        amended: _objectSpread(_objectSpread({}, service.status.amended), {}, {
          note
        })
      })
    });
  } else if (isAwaitingApproval(service.status)) {
    return _objectSpread(_objectSpread({}, service), {}, {
      status: _objectSpread(_objectSpread({}, service.status), {}, {
        awaitingApproval: _objectSpread(_objectSpread({}, service.status.awaitingApproval), {}, {
          note
        })
      })
    });
  }

  return service;
}
export function applyChanges(service) {
  return isAmended(service.status) ? applyChangesOnService(service, service.status.amended.changes) : isCompleted(service.status) ? applyChangesOnService(service, service.status.completed.changes) : service;
}

function applyChangesOnService(service, changes) {
  if (!changes) {
    return service;
  }

  return _objectSpread(_objectSpread({}, service), {}, {
    name: changes.name != null ? changes.name : service.name,
    description: changes.description != null ? changes.description : service.description,
    billing: changes.billing != null ? changes.billing : service.billing,
    cost: changes.cost != null ? changes.cost : service.cost
  });
}